// src/components/ActivityFeedView.jsx
// Activity feed showing recent changes across all deals

import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, LogOut, Activity, Building2, FileText, ArrowRightLeft, 
  Upload, Trash2, RotateCcw, Clock, Filter, RefreshCw, Loader2,
  ChevronDown, User, Calendar, MapPin
} from 'lucide-react';
import { useActivityFeed, useDealStages } from '../hooks/useSupabase';
import { getUpcomingVisits } from '../lib/supabase';

// LFG Brand Colors
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Activity type icons and colors
const ACTIVITY_TYPES = {
  deal_created: { icon: Building2, color: '#22c55e', label: 'Deal Created' },
  deal_updated: { icon: FileText, color: '#3b82f6', label: 'Deal Updated' },
  deal_stage_changed: { icon: ArrowRightLeft, color: '#8b5cf6', label: 'Stage Changed' },
  deal_deleted: { icon: Trash2, color: '#ef4444', label: 'Deal Deleted' },
  deal_restored: { icon: RotateCcw, color: '#22c55e', label: 'Deal Restored' },
  task_status_changed: { icon: Clock, color: '#f59e0b', label: 'Task Updated' },
  file_uploaded: { icon: Upload, color: '#06b6d4', label: 'File Uploaded' },
  file_deleted: { icon: Trash2, color: '#ef4444', label: 'File Deleted' },
  note_created: { icon: FileText, color: '#8b5cf6', label: 'Note Added' },
  visit_scheduled: { icon: Calendar, color: '#10b981', label: 'Visit Scheduled' },
};

export default function ActivityFeedView({
  organization,
  onBack,
  onSelectDeal,
  pc = LFG_COLORS.navy,
  sc = LFG_COLORS.gray,
  signOut,
  isIntegration = false,
}) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  
  const { activities, loading, hasMore, loadMore, refresh } = useActivityFeed(organization?.id, { limit: 50 });
  const { stages } = useDealStages(organization?.id);

  // Load upcoming visits
  useEffect(() => {
    if (organization?.id) {
      loadVisits();
    }
  }, [organization?.id]);

  const loadVisits = async () => {
    setVisitsLoading(true);
    try {
      const result = await getUpcomingVisits(organization?.id, 30);
      if (result.success) {
        setUpcomingVisits(result.visits || []);
      }
    } catch (err) {
      console.error('Failed to load visits:', err);
    } finally {
      setVisitsLoading(false);
    }
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    // Integration role can't see file-related activity — names could
    // expose financial / legal docs they shouldn't have visibility into.
    if (isIntegration && (activity.activity_type === 'file_uploaded' || activity.activity_type === 'file_deleted')) {
      return false;
    }
    // Type filter
    if (typeFilter !== 'all' && activity.activity_type !== typeFilter) return false;
    
    // Time filter
    if (timeFilter !== 'all') {
      const activityDate = new Date(activity.created_at);
      const now = new Date();
      const diffHours = (now - activityDate) / (1000 * 60 * 60);
      
      if (timeFilter === 'today' && diffHours > 24) return false;
      if (timeFilter === 'week' && diffHours > 168) return false;
      if (timeFilter === 'month' && diffHours > 720) return false;
    }
    
    return true;
  });

  // Group activities by date
  const groupedActivities = filteredActivities.reduce((groups, activity) => {
    const date = new Date(activity.created_at).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(activity);
    return groups;
  }, {});

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActivityIcon = (type) => {
    const config = ACTIVITY_TYPES[type] || { icon: Activity, color: sc };
    const IconComponent = config.icon;
    return <IconComponent className="w-4 h-4" style={{ color: config.color }} />;
  };

  const getActivityColor = (type) => {
    return ACTIVITY_TYPES[type]?.color || sc;
  };

  const getStageName = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.name || stageId;
  };

  const getStageColor = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.color || '#6b7280';
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      {/* Page heading + controls */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Activity
          </h1>
          <p className="text-sm mt-1" style={{ color: '#475569' }}>
            Every change across the portfolio — filter by type or time window
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="h-9 text-sm rounded-lg px-3 pr-8 cursor-pointer focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          >
            <option value="all">All Activity</option>
            {Object.entries(ACTIVITY_TYPES)
              .filter(([key]) => !isIntegration || (key !== 'file_uploaded' && key !== 'file_deleted'))
              .map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          <select
            value={timeFilter}
            onChange={e => setTimeFilter(e.target.value)}
            className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <button
            onClick={refresh}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg transition-colors hover:bg-slate-50"
            style={{ border: '1px solid #E5E9F0', color: '#475569', backgroundColor: 'white' }}
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Activity List */}
      <div>
        {/* Upcoming Visits Section */}
        {upcomingVisits.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                   style={{ backgroundColor: 'rgba(14, 165, 233, 0.1)', color: '#0EA5E9' }}>
                <Calendar className="w-3.5 h-3.5" strokeWidth={2.2} />
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#475569' }}>
                Upcoming Site Visits
              </span>
              <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                {upcomingVisits.length}
              </span>
            </div>
            <div className="rounded-2xl overflow-hidden"
                 style={{
                   backgroundColor: '#ffffff',
                   border: '1px solid #E5E9F0',
                   boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
                 }}>
              {upcomingVisits.map((visit, idx) => (
                <div
                  key={visit.id}
                  className="px-5 py-3.5 cursor-pointer transition-colors hover:bg-[#FAFBFD]"
                  style={idx > 0 ? { borderTop: '1px solid #F0F2F6' } : {}}
                  onClick={() => onSelectDeal && onSelectDeal(visit.deal_id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                         style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)', boxShadow: '0 2px 6px rgba(14, 165, 233, 0.25)' }}>
                      <Calendar className="w-4 h-4 text-white" strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>{visit.deals?.name || 'Unknown Deal'}</span>
                        <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                          {new Date(visit.visit_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="text-xs mt-0.5 flex items-center gap-2 tabular-nums" style={{ color: '#64748B' }}>
                        <span>{visit.start_time?.substring(0, 5)} – {visit.end_time?.substring(0, 5)}</span>
                        {visit.location && (
                          <>
                            <span style={{ color: '#CBD5E1' }}>·</span>
                            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {visit.location}</span>
                          </>
                        )}
                      </div>
                      {visit.created_by_name && (
                        <div className="text-[11px] mt-0.5 inline-flex items-center gap-1" style={{ color: '#94A3B8' }}>
                          <User className="w-3 h-3" /> Scheduled by {visit.created_by_name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && activities.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#94A3B8' }} />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="rounded-2xl text-center py-16"
               style={{
                 backgroundColor: '#ffffff',
                 border: '1px solid #E5E9F0',
                 boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
               }}>
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                 style={{ backgroundColor: 'rgba(26, 54, 101, 0.08)' }}>
              <Activity className="w-6 h-6" style={{ color: '#1A3665' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>No activity to show</p>
            <p className="text-xs mt-1" style={{ color: '#64748B' }}>
              Activity will appear here as changes are made to deals
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedActivities).map(([date, dayActivities]) => (
              <div key={date}>
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] mb-3 sticky top-16 py-1.5"
                     style={{
                       color: '#94A3B8',
                       backgroundColor: 'rgba(250, 251, 253, 0.9)',
                       backdropFilter: 'blur(6px)',
                     }}>
                  {date}
                </div>
                <div className="rounded-2xl overflow-hidden"
                     style={{
                       backgroundColor: '#ffffff',
                       border: '1px solid #E5E9F0',
                       boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
                     }}>
                  {dayActivities.map((activity, idx) => (
                    <div
                      key={activity.id}
                      className="px-5 py-3.5 transition-colors hover:bg-[#FAFBFD]"
                      style={idx > 0 ? { borderTop: '1px solid #F0F2F6' } : {}}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                             style={{ backgroundColor: `${getActivityColor(activity.activity_type)}18`, color: getActivityColor(activity.activity_type) }}>
                          {getActivityIcon(activity.activity_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm" style={{ color: '#334155' }}>
                            {activity.deals?.name && (
                              <span
                                className="font-semibold cursor-pointer transition-colors"
                                onClick={() => onSelectDeal(activity.deal_id)}
                                style={{ color: '#1A3665' }}
                                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                              >
                                {activity.deals.name}
                              </span>
                            )}
                            {activity.deals?.name && <span style={{ color: '#CBD5E1' }}> · </span>}
                            {activity.description}
                          </p>

                          {/* Stage change badges */}
                          {activity.activity_type === 'deal_stage_changed' && activity.metadata && (
                            <div className="mt-2">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor: `${getStageColor(activity.metadata.from_stage)}18`, color: getStageColor(activity.metadata.from_stage) }}>
                                  {getStageName(activity.metadata.from_stage)}
                                </span>
                                <ArrowRightLeft className="w-3 h-3" style={{ color: '#94A3B8' }} />
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor: `${getStageColor(activity.metadata.to_stage)}18`, color: getStageColor(activity.metadata.to_stage) }}>
                                  {getStageName(activity.metadata.to_stage)}
                                </span>
                              </div>
                              {activity.metadata.to_stage === 'canceled' && activity.metadata.canceled_reason && (
                                <p className="text-xs mt-2 px-2 py-1.5 rounded-md"
                                   style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
                                  Reason: {activity.metadata.canceled_reason}
                                </p>
                              )}
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-1.5 text-[11px] tabular-nums" style={{ color: '#94A3B8' }}>
                            <span>{formatTime(activity.created_at)}</span>
                            {activity.users?.full_name && (
                              <>
                                <span style={{ color: '#CBD5E1' }}>·</span>
                                <span className="inline-flex items-center gap-1">
                                  <User className="w-3 h-3" /> {activity.users.full_name}
                                </span>
                              </>
                            )}
                            {activity.metadata?.uploaded_by_type === 'seller' && (
                              <>
                                <span style={{ color: '#CBD5E1' }}>·</span>
                                <span style={{ color: '#D97706' }}>Uploaded by seller</span>
                              </>
                            )}
                          </div>
                        </div>

                        {activity.deal_id && (
                          <button
                            onClick={() => onSelectDeal(activity.deal_id)}
                            className="text-[11px] font-medium flex-shrink-0 transition-colors"
                            style={{ color: '#1A3665' }}
                            onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                          >
                            View Deal →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="text-center py-4">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="h-9 inline-flex items-center gap-1.5 px-4 text-sm font-medium rounded-lg transition-colors hover:bg-slate-50 disabled:opacity-50"
                  style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
